import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Check } from 'lucide-react';
import { buttonClass } from './Button';

const GAP = 4;
const EDGE = 8;
const ITEM_SELECTOR = '[role^="menuitem"]:not([aria-disabled="true"])';

const ROLE = { radio: 'menuitemradio', checkbox: 'menuitemcheckbox' };

const ITEM_CLASS = 'flex items-center gap-2 w-full min-h-8 px-2.5 py-1 rounded-control text-left text-body text-ink outline-none hover:bg-hover focus:bg-hover';

function MenuItem({ item, onDone }) {
  const role = ROLE[item.type] || 'menuitem';
  const checkable = Boolean(ROLE[item.type]);
  const content = (
    <>
      {checkable && (
        <span className="w-4 shrink-0 text-signal" aria-hidden="true">
          {item.checked && <Check size={15} strokeWidth={2.25} />}
        </span>
      )}
      <span className="flex-1 min-w-0 truncate">{item.label}</span>
      {item.href && <ArrowUpRight size={14} className="shrink-0 text-graphite" aria-hidden="true" />}
    </>
  );
  const common = {
    role,
    tabIndex: -1,
    className: ITEM_CLASS,
    'aria-checked': checkable ? Boolean(item.checked) : undefined,
  };

  if (item.to) {
    return (
      <Link to={item.to} {...common} onClick={() => onDone()}>
        {content}
      </Link>
    );
  }
  if (item.href) {
    return (
      <a href={item.href} target="_blank" rel="noopener noreferrer" {...common} onClick={() => onDone()}>
        {content}
      </a>
    );
  }
  return (
    <button
      type="button"
      {...common}
      onClick={() => {
        item.onSelect?.();
        if (!item.keepOpen) onDone();
      }}
    >
      {content}
    </button>
  );
}

/**
 * Button that opens a list of actions or choices.
 * items: [{ key, label, onSelect?, to?, href?, type?: 'radio' | 'checkbox', checked?, keepOpen? }]
 * A `{ key, divider: true }` item draws a rule between groups.
 */
export default function Menu({
  items,
  children,
  label,
  align = 'start',
  minWidth = 180,
  variant,
  size,
  square,
  triggerClassName,
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const close = useCallback((restoreFocus = true) => {
    setOpen(false);
    setPosition(null);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    const anchor = triggerRef.current.getBoundingClientRect();
    const menu = menuRef.current.getBoundingClientRect();
    const fitsBelow = anchor.bottom + GAP + menu.height <= window.innerHeight - EDGE;
    const top = fitsBelow ? anchor.bottom + GAP : Math.max(EDGE, anchor.top - GAP - menu.height);
    const preferred = align === 'end' ? anchor.right - menu.width : anchor.left;
    const left = Math.min(Math.max(EDGE, preferred), window.innerWidth - menu.width - EDGE);
    setPosition({ top, left });
  }, [open, align]);

  useEffect(() => {
    if (!open || !position) return;
    const menuItems = [...menuRef.current.querySelectorAll(ITEM_SELECTOR)];
    const checked = menuItems.find((el) => el.getAttribute('aria-checked') === 'true');
    (checked || menuItems[0] || menuRef.current).focus();
  }, [open, position]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (menuRef.current?.contains(event.target) || triggerRef.current?.contains(event.target)) return;
      close(false);
    };
    const onScroll = (event) => {
      if (menuRef.current?.contains(event.target)) return;
      close(false);
    };
    const onResize = () => close(false);
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, close]);

  const onMenuKeyDown = (event) => {
    const menuItems = [...menuRef.current.querySelectorAll(ITEM_SELECTOR)];
    const index = menuItems.indexOf(document.activeElement);
    const focusAt = (i) => menuItems[(i + menuItems.length) % menuItems.length]?.focus();

    if (event.key === 'ArrowDown') focusAt(index + 1);
    else if (event.key === 'ArrowUp') focusAt(index - 1);
    else if (event.key === 'Home') focusAt(0);
    else if (event.key === 'End') focusAt(menuItems.length - 1);
    else if (event.key === 'Escape') close();
    else if (event.key === 'Tab') close(false);
    else return;

    if (event.key !== 'Tab') event.preventDefault();
    event.stopPropagation();
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={square ? label : undefined}
        className={buttonClass({ variant, size, square, className: triggerClassName })}
        onClick={() => (open ? close() : setOpen(true))}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !open) {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        {children}
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            aria-label={label}
            tabIndex={-1}
            onKeyDown={onMenuKeyDown}
            className="fixed z-[3300] p-1 max-h-[60vh] overflow-y-auto bg-sheet border border-rule rounded-panel shadow-overlay outline-none"
            style={{ minWidth, top: position?.top ?? 0, left: position?.left ?? 0, visibility: position ? 'visible' : 'hidden' }}
          >
            {items.map((item) =>
              item.divider ? (
                <div key={item.key} role="separator" className="my-1 border-t border-rule" />
              ) : (
                <MenuItem key={item.key} item={item} onDone={close} />
              ),
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
